// 배당금 > 보유종목 — 배당을 받는 종목만 따로 관리 (매매일지와 분리)
// 종목·시장·코드(티커)·수량·증권사·과세 유형(일반/부분과세/비과세)·1주당 세금. 배당 입력 시 기본값, 이후 배당 예정 자동 생성의 기준.
import { useState, useMemo } from "react";
import { searchStocks } from "./kisApi";
import { Ico } from "./lib/icons";

const mono = { fontFamily: "'DM Mono', monospace" };
const ACCENT = "#a78bfa";
const newId = () => `h${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const normName = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
const EMPTY = { name: "", market: "KR", code: "", shares: "", broker: "", taxType: "normal", partialMode: "monthly", taxPerShare: "", exempt: false, memo: "" };
// 부분과세 방식: monthly 매달 달라짐(입력할 때 선택, 기본) / fixed 매번 같은 1주당 세금 자동 적용
export const partialModeOf = (h) => (h?.partialMode === "fixed" ? "fixed" : "monthly");
// 과세 유형: normal 일반(국내 15.4%·미국 15%) / partial 부분과세(커버드콜 등, 1주당 세금 직접) / exempt 비과세(ISA·연금)
export const taxTypeOf = (h) => (h?.taxType || (h?.exempt ? "exempt" : "normal"));
const TAX_TYPES = [
  { v: "normal", label: "일반 과세", hint: "국내 15.4% · 미국 15%" },
  { v: "partial", label: "부분과세", hint: "커버드콜 ETF 등 — 1주당 세금 입력" },
  { v: "exempt", label: "비과세", hint: "ISA·연금 계좌" },
];
const money = (v, market) => (v === "" || v == null ? "-" : market === "US" ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 })}` : `₩${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

const inputStyle = { width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", fontSize: 13, fontFamily: "inherit", outline: "none" };
const th = { padding: "10px 12px", fontSize: 11, color: "var(--text-sub)", fontWeight: 600, textAlign: "left", whiteSpace: "nowrap", borderBottom: "1px solid var(--border)", ...mono };
const td = { padding: "10px 12px", fontSize: 13, color: "var(--text)", borderBottom: "1px solid var(--divider-row)", whiteSpace: "nowrap" };
const btn = (primary) => ({
  padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
  border: primary ? "none" : "1px solid var(--border)",
  background: primary ? "linear-gradient(135deg, #a78bfa, #7c3aed)" : "transparent",
  color: primary ? "#fff" : "var(--text-sub)",
});

/** 매매일지 포지션(종목+증권사, 매수−매도 잔여) → 가져오기 후보. 보유 수량이 남은 것만 */
function holdingsFromJournal(krPositions = [], usPositions = []) {
  const out = [];
  krPositions.forEach(p => { if (p.isActive && p.name) out.push({ name: p.name, market: "KR", shares: p.qty, broker: p.broker || "" }); });
  usPositions.forEach(p => { if (p.isActive && p.name) out.push({ name: p.name, market: "US", shares: p.qty, broker: p.broker || "" }); });
  return out;
}
const candKey = (c) => `${c.market}|${normName(c.name)}|${c.broker || ""}`;

function HoldingForm({ initial, onSave, onCancel, brokers, suggestTaxPerShare }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, taxType: taxTypeOf(initial), partialMode: partialModeOf(initial) });
  const [looking, setLooking] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const lookupCode = async () => {
    if (f.market !== "KR" || !f.name.trim()) return;
    setLooking(true);
    const r = await searchStocks(f.name.trim());
    const hit = r.find(s => normName(s.name) === normName(f.name)) || r[0];
    if (hit) setF(p => ({ ...p, code: hit.code, name: p.name || hit.name }));
    setLooking(false);
  };
  const valid = f.name.trim() && parseFloat(f.shares) > 0;
  return (
    <div style={{ padding: 16, borderRadius: 12, border: "1px solid rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.05)", marginBottom: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <label><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>시장</div>
          <select value={f.market} onChange={e => set("market", e.target.value)} style={inputStyle}>
            <option value="KR">🇰🇷 국내</option><option value="US">🇺🇸 미국</option>
          </select></label>
        <label><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>종목명 *</div>
          <input value={f.name} onChange={e => set("name", e.target.value)} onBlur={() => { if (!f.code) lookupCode(); }} placeholder={f.market === "US" ? "예: SCHD" : "예: KB금융"} style={inputStyle} /></label>
        <label><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>{f.market === "US" ? "티커" : "종목코드"} {looking && <span style={{ color: ACCENT }}>찾는 중…</span>}</div>
          <input value={f.code} onChange={e => set("code", f.market === "US" ? e.target.value.toUpperCase() : e.target.value)} placeholder={f.market === "US" ? "SCHD" : "105560 (자동)"} style={{ ...inputStyle, ...mono }} /></label>
        <label><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>보유 수량 *</div>
          <input type="number" min="0" step="any" value={f.shares} onChange={e => set("shares", e.target.value)} style={{ ...inputStyle, ...mono, textAlign: "right" }} /></label>
        <label><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>증권사</div>
          <input list="div-holding-brokers" value={f.broker} onChange={e => set("broker", e.target.value)} style={inputStyle} />
          <datalist id="div-holding-brokers">{brokers.map(b => <option key={b} value={b} />)}</datalist></label>
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6 }}>과세 유형</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "stretch" }}>
          {TAX_TYPES.map(t => (
            <label key={t.v} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, cursor: "pointer",
              border: `1px solid ${f.taxType === t.v ? "rgba(167,139,250,0.6)" : "var(--border)"}`, background: f.taxType === t.v ? "rgba(167,139,250,0.1)" : "transparent" }}>
              <input type="radio" name="taxType" checked={f.taxType === t.v} onChange={() => set("taxType", t.v)} />
              <span><span style={{ fontSize: 12, color: "var(--text)", fontWeight: 600 }}>{t.label}</span><br /><span style={{ fontSize: 10, color: "var(--text-sub)" }}>{t.hint}</span></span>
            </label>
          ))}
          {f.taxType === "partial" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, justifyContent: "center" }}>
              {[{ v: "monthly", l: "매달 달라짐", h: "배당 입력할 때 그 달 값 선택" }, { v: "fixed", l: "매번 같음", h: "아래 값을 자동 적용" }].map(o => (
                <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "var(--text)" }}>
                  <input type="radio" name="partialMode" checked={f.partialMode === o.v} onChange={() => set("partialMode", o.v)} />
                  {o.l} <span style={{ fontSize: 10, color: "var(--text-sub)" }}>{o.h}</span>
                </label>
              ))}
            </div>
          )}
          {f.taxType === "partial" && (
            <label style={{ flex: "1 1 180px", minWidth: 180 }}>
              <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>{f.partialMode === "fixed" ? "1주당 세금" : "1주당 세금 기본값 (선택)"} ({f.market === "US" ? "$" : "원"})</div>
              <input type="number" min="0" step="any" value={f.taxPerShare} onChange={e => set("taxPerShare", e.target.value)}
                placeholder={suggestTaxPerShare != null ? `최근 배당 기준 ${suggestTaxPerShare}` : "예: 12"} style={{ ...inputStyle, ...mono, textAlign: "right" }} />
              {suggestTaxPerShare != null && String(f.taxPerShare) === "" && (
                <button type="button" onClick={() => set("taxPerShare", String(suggestTaxPerShare))} style={{ marginTop: 4, padding: 0, border: "none", background: "none", color: ACCENT, fontSize: 11, cursor: "pointer" }}>
                  최근 배당 기록 값({suggestTaxPerShare}) 쓰기
                </button>
              )}
            </label>
          )}
        </div>
      </div>
      <label style={{ display: "block", marginTop: 10 }}><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4 }}>메모</div>
        <input value={f.memo} onChange={e => set("memo", e.target.value)} placeholder="예: 월배당, 커버드콜" style={inputStyle} /></label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button onClick={onCancel} style={btn(false)}>취소</button>
        <button disabled={!valid} onClick={() => onSave({
          ...f, name: f.name.trim(), code: f.code.trim(), shares: parseFloat(f.shares),
          exempt: f.taxType === "exempt",
          taxPerShare: f.taxType === "partial" && String(f.taxPerShare).trim() !== "" ? parseFloat(f.taxPerShare) : "",
        })} style={{ ...btn(true), opacity: valid ? 1 : 0.4 }}>저장</button>
      </div>
    </div>
  );
}

export default function DividendHoldings({ holdings = [], onChange, krPositions, usPositions, dividends = [], rate, brokers = [] }) {
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [importOpen, setImportOpen] = useState(false);
  const [picked, setPicked] = useState({});
  const [resolving, setResolving] = useState(false);
  const thisYear = String(new Date(Date.now() + 9 * 3600 * 1000).getUTCFullYear());

  // 종목별 올해 받은 배당 / 최근 수령일
  const received = useMemo(() => {
    const m = {};
    dividends.forEach(d => {
      const k = normName(d.name);
      if (!k) return;
      const r = m[k] || { yearKrw: 0, last: "", lastTaxPerShare: null, lastTaxDate: "", tpsHist: [] };
      if ((d.date || "").startsWith(thisYear)) {
        const amt = parseFloat(d.amount) || 0;
        r.yearKrw += d.currency === "USD" ? amt * (parseFloat(d.rate) > 0 ? parseFloat(d.rate) : rate) : amt;
      }
      if ((d.date || "") > r.last) r.last = d.date;
      // 실제 낸 세금 ÷ 주수 = 1주당 세금 (가장 최근 배당 기준)
      const tax = parseFloat(d.tax), sh = parseFloat(d.shares);
      if (!d.taxExempt && Number.isFinite(tax) && Number.isFinite(sh) && sh > 0) {
        const tps = +(tax / sh).toFixed(d.currency === "USD" ? 4 : 2);
        if ((d.date || "") >= r.lastTaxDate) { r.lastTaxPerShare = tps; r.lastTaxDate = d.date || ""; }
        // 최근 12개월 범위 (월배당 부분과세 변동 확인용)
        if ((d.date || "") >= new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10)) r.tpsHist.push(tps);
      }
      m[k] = r;
    });
    return m;
  }, [dividends, rate, thisYear]);

  const candidates = useMemo(() => {
    const have = new Set(holdings.map(candKey));
    return holdingsFromJournal(krPositions, usPositions).filter(c => !have.has(candKey(c)));
  }, [holdings, krPositions, usPositions]);

  const save = (item) => {
    if (editing === "new") onChange([...holdings, { ...item, id: newId() }]);
    else onChange(holdings.map(h => (h.id === editing ? { ...h, ...item } : h)));
    setEditing(null);
  };
  const remove = (id) => { if (window.confirm("이 종목을 배당 보유종목에서 뺄까요? (배당 내역은 그대로 남아요)")) onChange(holdings.filter(h => h.id !== id)); };

  const doImport = async () => {
    const chosen = candidates.filter(c => picked[candKey(c)]);
    if (!chosen.length) return;
    setResolving(true);
    const items = [];
    for (const c of chosen) {
      let code = "";
      if (c.market === "KR") {
        const r = await searchStocks(c.name);
        const hit = r.find(s => normName(s.name) === normName(c.name));
        code = hit ? hit.code : "";
      } else if (/^[A-Z.\-]{1,6}$/.test(c.name.trim().toUpperCase())) {
        code = c.name.trim().toUpperCase();
      }
      items.push({ ...EMPTY, ...c, code, id: newId() });
    }
    onChange([...holdings, ...items]);
    setResolving(false); setImportOpen(false); setPicked({});
  };

  const totalYear = holdings.reduce((a, h) => a + ((received[normName(h.name)] || received[normName(h.code)])?.yearKrw || 0), 0);
  const exemptCount = holdings.filter(h => taxTypeOf(h) === "exempt").length;
  const partialCount = holdings.filter(h => taxTypeOf(h) === "partial").length;
  const noCode = holdings.filter(h => !h.code).length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: ACCENT }}>배당 보유종목</div>
          <div style={{ fontSize: 12, color: "var(--text-sub)", marginTop: 4 }}>배당을 받는 종목만 따로 관리해요. 배당 입력 시 수량·증권사·비과세가 자동으로 채워집니다.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setImportOpen(v => !v); setEditing(null); }} style={btn(false)}><Ico n="download" />매매일지에서 불러오기</button>
          <button onClick={() => { setEditing("new"); setImportOpen(false); }} style={btn(true)}>+ 종목 추가</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "보유 종목", value: `${holdings.length}개`, sub: `국내 ${holdings.filter(h => h.market === "KR").length} · 미국 ${holdings.filter(h => h.market === "US").length}` },
          { label: `${thisYear}년 받은 배당 (세전, 원화)`, value: `₩${Math.round(totalYear).toLocaleString()}`, color: "#34d399" },
          { label: "비과세 · 부분과세", value: `${exemptCount} · ${partialCount}개`, sub: "비과세는 금융소득 합산 제외 · 부분과세는 과세분만 합산" },
        ].map(s => (
          <div key={s.label} style={{ padding: "14px 16px", borderRadius: 12, border: "1px solid var(--border)", background: "var(--bg-card)" }}>
            <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color || "var(--text)", ...mono }}>{s.value}</div>
            {s.sub && <div style={{ fontSize: 11, color: "var(--text-sub)", marginTop: 4 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {importOpen && (
        <div style={{ padding: 16, borderRadius: 12, border: "1px solid var(--border)", background: "var(--bg-card)", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>매매일지 보유현황에서 가져오기</div>
          {candidates.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-sub)" }}>가져올 종목이 없어요. (매매일지에 보유중인 종목이 없거나 이미 모두 추가됨)</div>
          ) : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                {candidates.map(c => {
                  const k = candKey(c);
                  return (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, border: `1px solid ${picked[k] ? "rgba(167,139,250,0.6)" : "var(--border)"}`, cursor: "pointer", fontSize: 12, color: "var(--text)" }}>
                      <input type="checkbox" checked={!!picked[k]} onChange={e => setPicked(p => ({ ...p, [k]: e.target.checked }))} />
                      {c.market === "US" ? "🇺🇸" : "🇰🇷"} {c.name} <span style={{ color: "var(--text-sub)", ...mono }}>{c.shares}주</span>{c.broker && <span style={{ color: "var(--text-muted)", fontSize: 11 }}>· {c.broker}</span>}
                    </label>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setPicked(Object.fromEntries(candidates.map(c => [candKey(c), true])))} style={btn(false)}>전체 선택</button>
                <button onClick={doImport} disabled={resolving} style={btn(true)}>{resolving ? "종목코드 찾는 중…" : "선택한 종목 추가"}</button>
              </div>
            </>
          )}
        </div>
      )}

      {editing === "new" && <HoldingForm initial={EMPTY} onSave={save} onCancel={() => setEditing(null)} brokers={brokers} />}

      {holdings.length === 0 && editing !== "new" ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)", border: "1px dashed var(--border)", borderRadius: 12 }}>
          아직 배당 보유종목이 없어요. <b style={{ color: ACCENT }}>+ 종목 추가</b> 또는 <b style={{ color: ACCENT }}>매매일지에서 불러오기</b>로 시작하세요.
        </div>
      ) : (
        <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--bg-card)" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
              <thead style={{ background: "var(--bg-table-head)" }}>
                <tr>
                  <th style={th}>종목</th><th style={th}>코드/티커</th><th style={{ ...th, textAlign: "right" }}>보유 수량</th>
                  <th style={th}>증권사</th><th style={th}>과세 유형</th><th style={{ ...th, textAlign: "right" }}>1주당 세금</th>
                  <th style={{ ...th, textAlign: "right" }}>{thisYear} 받은 배당</th><th style={th}>최근 수령</th><th style={{ ...th, textAlign: "center" }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {[...holdings].sort((a, b) => (a.market + a.name).localeCompare(b.market + b.name)).map(h => {
                  if (editing === h.id) {
                    return <tr key={h.id}><td colSpan={9} style={{ padding: 8 }}><HoldingForm initial={h} onSave={save} onCancel={() => setEditing(null)} brokers={brokers} suggestTaxPerShare={(received[normName(h.name)] || received[normName(h.code)])?.lastTaxPerShare ?? null} /></td></tr>;
                  }
                  const r = (received[normName(h.name)] || received[normName(h.code)]);
                  const tt = taxTypeOf(h);
                  return (
                    <tr key={h.id}>
                      <td style={{ ...td, fontWeight: 600 }}>{h.market === "US" ? "🇺🇸" : "🇰🇷"} {h.name}{h.memo && <div style={{ fontSize: 11, color: "var(--text-sub)", fontWeight: 400, marginTop: 2 }}>{h.memo}</div>}</td>
                      <td style={{ ...td, ...mono, color: h.code ? "var(--text-sub)" : "#fbbf24" }}>{h.code || "미입력"}</td>
                      <td style={{ ...td, ...mono, textAlign: "right" }}>{Number(h.shares).toLocaleString()}</td>
                      <td style={td}>{h.broker || <span style={{ color: "var(--text-muted)" }}>-</span>}</td>
                      <td style={td}>{tt === "exempt"
                        ? <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 5, color: "#34d399", border: "1px solid rgba(52,211,153,0.4)" }}>비과세</span>
                        : tt === "partial"
                          ? <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 5, color: "#fbbf24", border: "1px solid rgba(251,191,36,0.4)" }}>부분과세</span>
                          : <span style={{ fontSize: 11, color: "var(--text-sub)" }}>일반</span>}</td>
                      <td style={{ ...td, ...mono, textAlign: "right" }}>
                        {tt === "exempt" ? <span style={{ color: "var(--text-muted)" }}>0</span>
                          : tt === "partial" && partialModeOf(h) === "monthly" ? (
                            <span style={{ color: "#fbbf24" }} title="최근 12개월 배당 기록 기준">
                              <span style={{ fontSize: 10, fontWeight: 600 }}>매달 변동</span>
                              {r?.tpsHist?.length > 0 && <div style={{ fontSize: 11 }}>{money(Math.min(...r.tpsHist), h.market)} ~ {money(Math.max(...r.tpsHist), h.market)}</div>}
                            </span>)
                          : tt === "partial" && h.taxPerShare !== "" && h.taxPerShare != null ? <span style={{ color: "#fbbf24", fontWeight: 600 }}>{money(h.taxPerShare, h.market)}</span>
                          : r?.lastTaxPerShare != null ? <span style={{ color: "var(--text-sub)" }} title={`${r.lastTaxDate} 배당 기록 기준`}>{money(r.lastTaxPerShare, h.market)}<span style={{ fontSize: 10, color: "var(--text-muted)" }}> 최근</span></span>
                          : <span style={{ color: "var(--text-muted)" }}>-</span>}
                      </td>
                      <td style={{ ...td, ...mono, textAlign: "right", color: r?.yearKrw ? "#34d399" : "var(--text-muted)" }}>{r?.yearKrw ? `₩${Math.round(r.yearKrw).toLocaleString()}` : "-"}</td>
                      <td style={{ ...td, ...mono, color: "var(--text-sub)" }}>{r?.last || "-"}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <button onClick={() => { setEditing(h.id); setImportOpen(false); }} style={{ ...btn(false), padding: "4px 10px", marginRight: 4 }}>수정</button>
                        <button onClick={() => remove(h.id)} style={{ ...btn(false), padding: "4px 10px", color: "#fb7185", borderColor: "rgba(251,113,133,0.3)" }}>삭제</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {noCode > 0 && <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 10 }}><Ico tint={false} n="triangle-alert" />코드/티커가 없는 종목 {noCode}개 — 다음 단계(배당 예정 자동 생성)에서 배당 일정을 찾으려면 코드가 필요해요.</div>}
    </div>
  );
}

// 배당 입력 모달에서 종목명을 고르면 보유종목 정보로 빈 칸 채우기
export function applyHoldingDefaults(form, holdings = []) {
  const h = holdings.find(x => normName(x.name) === normName(form.name));
  if (!h) return form;
  const tt = taxTypeOf(h);
  return {
    ...form,
    shares: form.shares === "" || form.shares == null ? String(h.shares) : form.shares,
    broker: form.broker ? form.broker : (h.broker || ""),
    currency: h.market === "US" ? "USD" : "KRW",
    taxExempt: tt === "exempt" ? true : form.taxExempt,
    // 고정(매번 같음)일 때만 자동 적용. 매달 달라짐이면 입력 창의 빠른 선택 버튼으로 고름
    taxPerShare: tt === "partial" && partialModeOf(h) === "fixed" && (form.taxPerShare === "" || form.taxPerShare == null) && h.taxPerShare !== "" && h.taxPerShare != null
      ? String(h.taxPerShare) : form.taxPerShare,
  };
}

